import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  LayoutDashboard,
  Zap,
  Calendar,
  MessageSquare,
  TrendingUp,
  Users,
  Globe,
  CreditCard,
  Settings,
  LogOut,
  ChevronLeft,
  ChevronRight,
  Sparkles,
  Code
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';

const Sidebar = ({ collapsed, setCollapsed }) => {
  const location = useLocation();
  const { user, logout } = useAuth();
  const { t } = useLanguage();

  const menuItems = [
    { icon: LayoutDashboard, label: t('sidebar.home'), path: '/dashboard' },
    { icon: Zap, label: t('sidebar.automation'), path: '/dashboard/automations' },
    { icon: Calendar, label: t('sidebar.calendar'), path: '/dashboard/calendar' },
    { icon: MessageSquare, label: t('sidebar.conversations'), path: '/dashboard/conversations' },
    { icon: TrendingUp, label: t('sidebar.leads'), path: '/dashboard/leads' },
    { icon: Users, label: t('sidebar.contacts'), path: '/dashboard/contacts' },
    { icon: Code, label: t('sidebar.webWidget'), path: '/dashboard/widget' },
    { icon: Globe, label: t('sidebar.bookingSite'), path: '/dashboard/website' },
    { icon: CreditCard, label: t('sidebar.billing'), path: '/dashboard/billing' },
    { icon: Settings, label: t('sidebar.settings'), path: '/dashboard/settings' },
  ];

  return (
    <aside className={`fixed left-0 top-0 h-full bg-white text-slate-600 border-r border-slate-100 transition-all duration-300 z-50 ${collapsed ? 'w-20' : 'w-64'}`}>
      <div className="flex flex-col h-full">
        {/* Logo */}
        <div className="p-4 flex items-center justify-between mb-4">
          {!collapsed ? (
            <Link to="/dashboard" className="flex items-center px-2">
              <img
                src="/logo-azul-transparente.png"
                alt="ChatAlfa"
                className="h-32 w-auto object-contain"
              />
            </Link>
          ) : (
            <div className="mx-auto mt-2">
              <Sparkles className="h-6 w-6 text-blue-500" />
            </div>
          )}
        </div>

        {/* Upgrade Button */}
        {!collapsed && (
          <div className="px-4 mb-6">
            <Link to="/dashboard/billing">
              <button className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded-xl text-sm transition-colors shadow-lg shadow-blue-500/20">
                {t('sidebar.upgrade')}
              </button>
            </Link>
          </div>
        )}

        {/* Menu Items */}
        <nav className="flex-1 px-3 space-y-1">
          {menuItems.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path;
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all group ${isActive
                  ? 'bg-blue-50 text-blue-600'
                  : 'hover:bg-slate-50 text-slate-600 hover:text-slate-900'
                  }`}
              >
                <Icon className={`h-5 w-5 flex-shrink-0 transition-colors ${isActive ? 'text-blue-600' : 'text-slate-400 group-hover:text-slate-600'}`} />
                {!collapsed && <span className="text-sm font-medium">{item.label}</span>}
                {isActive && !collapsed && <div className="ml-auto w-1.5 h-1.5 rounded-full bg-blue-600 shadow-glow" />}
              </Link>
            );
          })}
        </nav>

        {/* Bottom Section */}
        <div className="p-4 border-t border-slate-100 mt-auto">
          <div className={`flex items-center gap-3 ${collapsed ? 'justify-center' : ''} mb-4`}>
            {user?.picture ? (
              <img src={user.picture} alt={user.name} className="w-8 h-8 rounded-full border border-slate-200" />
            ) : (
              <div className="w-8 h-8 rounded-full bg-blue-600 flex items-center justify-center text-white text-xs font-bold border border-slate-200">
                {user?.name?.[0] || 'U'}
              </div>
            )}
            {!collapsed && (
              <div className="flex-1 min-w-0">
                <p className="text-sm font-bold text-slate-900 truncate">{user?.name}</p>
                <p className="text-xs text-slate-500 truncate">{user?.email}</p>
              </div>
            )}
          </div>

          <button
            onClick={logout}
            className={`w-full flex items-center gap-3 px-3 py-2 rounded-xl text-slate-500 hover:bg-red-50 hover:text-red-500 transition-all ${collapsed ? 'justify-center' : ''}`}
          >
            <LogOut className="h-4 w-4" />
            {!collapsed && <span className="text-sm font-medium">{t('sidebar.logout')}</span>}
          </button>
        </div>

        {/* Collapse Toggle */}
        <button
          onClick={() => setCollapsed(!collapsed)}
          className="absolute -right-3 top-20 w-6 h-6 bg-white border border-slate-200 rounded-full flex items-center justify-center text-slate-400 hover:text-slate-600 shadow-sm z-50"
        >
          {collapsed ? <ChevronRight className="h-3 w-3" /> : <ChevronLeft className="h-3 w-3" />}
        </button>
      </div>
    </aside>
  );
};

export default Sidebar;
